import React from 'react'
import styled from 'styled-components'
import { FormattedMessage } from 'gatsby-plugin-intl'
import { breakpoint } from 'src/theme/grid'
import ReactMarkdown from 'react-markdown'
import CircularProgress from '@material-ui/core/CircularProgress'
import { ButtonWithLoader } from 'src/components/atoms/Buttons'
import { MARKETPLACEOFFER_TRACKING_EVENT_TYPES } from 'src/ducks/consts'
import useOfferTracking from 'src/features/marketplace/hooks/useMarketplaceOfferTracking'

const Loader = styled(CircularProgress)`
  width: 25px !important;
  height: 25px !important;
  vertical-align: middle;
  color: white !important;
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: white;
  border-radius: 0;
  padding: 2rem;
  border-left: 5px solid ${({ theme }) => theme.colors.apple.hex()};
`

const LearnMore = ({ offerData }) => {
  const { trackEvent, isLoading } = useOfferTracking()

  const handleClick = async () => {
    if (!offerData || !offerData.id) {
      // If a redirect URL exists, try redirecting even without tracking.
      if (offerData && offerData.landingPageUrl) {
        window.open(offerData.landingPageUrl, '_blank')
      }
      return
    }

    try {
      await trackEvent(
        offerData.id,
        MARKETPLACEOFFER_TRACKING_EVENT_TYPES.OFFER_CLICKED,
        () => {
          if (offerData.landingPageUrl) {
            window.open(offerData.landingPageUrl, '_blank')
          }
        }
      )
    } catch (error) {
      // Try redirecting even when tracking fails.
      if (offerData.landingPageUrl) {
        window.open(offerData.landingPageUrl, '_blank')
      }
    }
  }

  return (
    <LearnMoreButton active={true} loading={isLoading} onClick={handleClick}>
      <FormattedMessage
        id='marketplace.learnMore'
        defaultMessage='Go to offer'
      />
    </LearnMoreButton>
  )
}

const StyledMarkdown = styled(ReactMarkdown)`
  h2 {
    color: ${({ theme }) => theme.colors.apple.hex()};
    margin-top: 0;
    margin-bottom: 0.5rem;
    font-size: 1.17em;
    font-weight: 600;

    strong {
      color: inherit;
    }
  }

  ul {
    padding-left: 1.5rem;
  }
`

const StyledTitle = styled(ReactMarkdown)`
  margin: 0;
  color: ${({ theme }) => theme.colors.apple.hex()};
  font-weight: 600;
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  line-height: 1.2;
  word-wrap: break-word;
  hyphens: auto;
  width: 100%;

  ${breakpoint.s`
    font-size: clamp(1.5rem, 4vw, 2rem);
  `}

  ${breakpoint.m`
    font-size: clamp(1.8rem, 5vw, 2.5rem);
  `}

  p {
    margin: 0;
    padding: 0;
  }
`

const OfferDetail = ({ offer, onClose, isLoading }) => {
  if (isLoading) {
    return (
      <DetailOverlay onClick={onClose}>
        <LoaderContainer onClick={e => e.stopPropagation()}>
          <Loader />
        </LoaderContainer>
      </DetailOverlay>
    )
  }

  if (!offer || !offer.data) return null

  const offerData = offer.data

  const handleContainerClick = e => {
    e.stopPropagation()
  }

  // Use offerData.content directly instead of decodedContent.
  const content = offerData.content || ''

  const hasLogo = offerData.logo?.url && offerData.logo.url.trim() !== ''
  const hasImage = offerData.image?.url && offerData.image.url.trim() !== ''

  const showHeaderLogo = hasLogo && hasImage

  const landingPageUrl =
    (typeof offerData.landingPageUrl === 'string' &&
      offerData.landingPageUrl.trim()) ||
    null

  return (
    <DetailOverlay onClick={onClose}>
      <DetailContainer onClick={handleContainerClick}>
        <StickyHeader>
          <CloseButton onClick={onClose}>×</CloseButton>
          {hasImage ? (
            <DetailImage src={offerData.image.url} alt={offerData.title} />
          ) : (
            <LogoContainer>
              {hasLogo && (
                <img src={offerData.logo.url} alt={offerData.title} />
              )}
            </LogoContainer>
          )}
          {landingPageUrl && <LearnMore offerData={offerData} />}
        </StickyHeader>
        <DetailContent>
          <DetailHeader hasImage={hasImage}>
            <HeaderLeft fullWidth={!showHeaderLogo}>
              <DetailTitle>
                <StyledTitle>{offerData.title}</StyledTitle>
              </DetailTitle>
            </HeaderLeft>
            {showHeaderLogo && hasImage && (
              <HeaderRight hasLogo={hasLogo}>
                <DetailLogo src={offerData.logo.url} alt='Logo' />
              </HeaderRight>
            )}
          </DetailHeader>
          <DetailDivider />
          {offerData.content_html ? (
            <DetailDescription
              dangerouslySetInnerHTML={{ __html: offerData.content_html }}
            />
          ) : content && /<[a-z][\s\S]*>/i.test(content) ? (
            <DetailDescription dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            <DetailDescription>
              <StyledMarkdown>{content}</StyledMarkdown>
            </DetailDescription>
          )}
        </DetailContent>
      </DetailContainer>
    </DetailOverlay>
  )
}

export default OfferDetail

const DetailOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`

const DetailContainer = styled.div`
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: white;
  border-radius: 0;
  position: relative;
  border-left: 5px solid ${({ theme }) => theme.colors.apple.hex()};
  padding-right: 7px;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #8b8b8b;
    border-radius: 0px;
    border: 0px solid transparent;
    background-clip: padding-box;
    min-height: 40px;

    &:hover {
      background: #757575;
      border: 0px solid transparent;
      background-clip: padding-box;
    }
  }
`

const DetailImage = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 10/4;
  object-fit: cover;
  display: block;
`

const DetailContent = styled.div`
  padding: 1.5rem;
  position: relative;
  box-shadow: 0 -4px 8px -4px rgba(0, 0, 0, 0.15);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`

const DetailDivider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.lightGrey.hex()};
  margin: 0.5rem 1.5rem 2rem;
  opacity: 0.7;
`

const DetailTitle = styled.div`
  flex-direction: column;
  margin: 0.5rem 0;
  display: flex;
  align-items: flex-start;
  text-align: left;
  min-height: auto;
  justify-content: flex-start;
  width: 100%;
`

const OfferHook = styled.div`
  display: inline-block;
  background-color: ${({ theme }) => theme.colors.apple.hex()};
  color: white;
  padding: 0.3rem 0.6rem;
  margin: 0.5rem 0;
  font-weight: bold;
  font-size: 0.9rem;
`

const DetailDescription = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.black};
  line-height: 1.6;

  p {
    margin-bottom: 1rem;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: -1px;
  background: ${({ theme }) => theme.colors.apple.hex()};
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 11;

  &:hover {
    background: ${({ theme }) => theme.colors.deYork.hex()};
  }
`

const LearnMoreButton = styled(ButtonWithLoader)`
  position: absolute;
  bottom: 0;
  right: 1.5rem;
  transform: translateY(50%);
  background-color: ${({ theme }) => theme.colors.apple.hex()};
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 0;
  font-weight: bold;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  z-index: 12;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: ${({ theme }) => theme.colors.deYork.hex()};
  }
`

const LogoContainer = styled.div`
  width: 100%;
  aspect-ratio: 10/4;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0;

  img {
    max-width: 80%;
    max-height: 80%;
    object-fit: contain;
  }
`

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
  box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.15);
  width: calc(100% + 7px);
  margin-right: -7px;
`

const DetailHeader = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: ${({ hasImage }) => (hasImage ? 'space-between' : 'center')};
  gap: 1rem;
  margin: 0.5rem 1.5rem 1.5rem 0;
  padding-right: 0.5rem;
  position: relative;
`

const HeaderLeft = styled.div`
  display: flex;
  margin-top: 0;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'calc(100% - 140px)')};
  min-width: 200px;
  padding-right: ${({ fullWidth }) => (fullWidth ? '0' : '2rem')};
  padding-left: 0;
  flex-direction: column;
`

const HeaderRight = styled.div`
  flex-shrink: 0;
  width: 100px;
  display: ${({ hasLogo }) => (hasLogo ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  margin-top: 0;
  align-self: center;
  padding-right: 0.5rem;
`

const DetailLogo = styled.img`
  max-width: 160px;
  max-height: 120px;
  object-fit: contain;
  background: white;
  padding: 0;
  margin: 0;
  box-sizing: content-box;
`
